import { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { ChevronLeft } from 'lucide-react'
import useApiService, {
  COMPANY_CATEGORIES_QUERY_KEY,
} from '../../../services/api'
import { Category } from '../../../models/companies'
import { useMobileSearch } from '../../../components/MobileSearch/hooks/useMobileSearch'
import { useMobileSearchFilterStore } from '../../../components/MobileSearch/hooks/useMobileSearchFilterStore'
import {
  getMobileSearchTypeOptions,
  isFbSearchType,
} from '../../../components/MobileSearch/hooks/getMobileSearchTypeOptions'
import type { MobileSearchFilters as MobileSearchFiltersType } from '../../../components/MobileSearch/hooks/useMobileSearch'
import { MobileSearchDropdown } from '../../../components/MobileSearch/MobileSearchDropdown'
import { MobileSearchInput } from '../../../components/MobileSearch/MobileSearchInput'
import { MobileSearchResults } from '../../../components/MobileSearch/MobileSearchResults'
import { MobileCompanySearchFilterset } from '../../../components/MobileSearch/MobileCompanySearchFilterset'
import { MobileSearchSort } from '../../../components/MobileSearch/MobileSearchSort'
import { MobileFiltersModal } from '../../../components/MobileFiltersModal'
import { CreateSavedFilterModal } from '../../../components/Filters/saved-filters/CreateSavedFilterModal'
import { countActiveFilters } from '../../../components/Filters/filterCount'
import { FilterChips } from '../../../components/Filters/components/FilterChips/FilterChips'
import { BackButton } from '../../../components/MobileSearch/MobileSearch.styles'
import {
  ContactCompanySearchResult,
  DoorSearchResult,
} from '../../../models/companies'
import { SearchType } from 'components/MobileSearch/hooks/useMobileSearchAPI'
import { useMountEffect } from '../../../hooks/useMountEffect'

const SEARCH_FILTER_IDENTIFIER = 'MOBILE_COMPANY_SEARCH' as const

interface SearchPageProps {
  hideBack?: boolean
  onClick?: (company: ContactCompanySearchResult | DoorSearchResult) => void
  options?: { value: SearchType; label: string }[]
  initialQuery?: string
  selectedItemId?: string | number
  /**
   * Optional client-side predicate to narrow which results are shown. Used by
   * the mobile contact-create company step to show only results it can resolve
   * to a contact_company (e.g. restaurants + custom companies). Default: show
   * all. (Filters the loaded page client-side; the server count is unchanged.)
   */
  resultFilter?: (
    result: ContactCompanySearchResult | DoorSearchResult
  ) => boolean
}

export default function SearchPage({
  hideBack = false,
  onClick,
  options,
  initialQuery,
  selectedItemId,
  resultFilter,
}: SearchPageProps) {
  const navigate = useNavigate()
  const api = useApiService()
  const queryClient = useQueryClient()
  const { data: categoriesData } = api.useGetActiveCompanyCategoriesAndTypes()
  const {
    state,
    setSearchType,
    setQuery,
    setFilters,
    setSort,
    clearSearch,
    loadMore,
  } = useMobileSearch()

  useMountEffect(() => {
    if (initialQuery) setQuery(initialQuery)
  })

  // When applying a filter that removes FB data, drop any FB search type so the
  // user isn't stranded on an empty First Bite search (event-handler, no effect).
  const handleApplyFilters = useCallback(
    (filters: MobileSearchFiltersType) => {
      setFilters(filters)
      const nextOptions =
        options ?? getMobileSearchTypeOptions(categoriesData, filters)
      if (
        isFbSearchType(state.searchType) &&
        !nextOptions.some((opt) => opt.value === state.searchType)
      ) {
        setSearchType('custom_company')
      }
    },
    [options, categoriesData, state.searchType, setFilters, setSearchType]
  )

  // Resolve category / type slugs to their display names so chips seeded from
  // the URL read "Fast Casual" instead of the raw "fast-casual" slug. Mirrors
  // the option labels built in MobileCompanySearchFilterset (incl. the "Other"
  // sub-category prefix) so a chip matches its modal option.
  //
  // Reads the categories straight from the query cache (memoized by data
  // reference) rather than closing over `categoriesData`: the store's label
  // backfill runs from a synchronous query-cache subscription that fires
  // before this component re-renders, so a captured value would still be stale
  // on the exact cold deep-link it needs to fix.
  const labelMapsRef = useRef<{
    data: Category[]
    category: Map<string, string>
    type: Map<string, string>
  } | null>(null)
  const resolveFilterLabel = useCallback(
    (key: 'category' | 'type', slug: string): string => {
      const data = queryClient.getQueryData<Category[]>(
        COMPANY_CATEGORIES_QUERY_KEY
      )
      if (!data) return slug
      if (labelMapsRef.current?.data !== data) {
        const category = new Map<string, string>()
        const type = new Map<string, string>()
        data.forEach((c) => {
          category.set(c.slug, c.name)
          c.sub_categories.forEach((sub) => {
            type.set(
              sub.slug,
              sub.name === 'Other' ? `${c.name} - Other` : sub.name
            )
          })
        })
        labelMapsRef.current = { data, category, type }
      }
      const maps = labelMapsRef.current
      return (key === 'category' ? maps.category : maps.type).get(slug) ?? slug
    },
    [queryClient]
  )

  // Shared Filters store (FIR-7790): the filter modal + chips read/write here,
  // and applied category / type changes are pushed back into the search state.
  const filterStore = useMobileSearchFilterStore(
    SEARCH_FILTER_IDENTIFIER,
    state.filters,
    handleApplyFilters,
    resolveFilterLabel
  )
  const {
    applyFilters,
    syncPendingFilters,
    valueFilters,
    rangeFilters,
    search: appliedSearch,
    zipCodeFilter,
  } = filterStore()

  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isSortOpen, setIsSortOpen] = useState(false)
  const [isSaveFilterOpen, setIsSaveFilterOpen] = useState(false)

  // The save-filter affordance mirrors desktop: it only appears once filters
  // are actually applied, and CreateSavedFilterModal snapshots the applied
  // filter state. Derived from the store's applied layers (not the two search
  // keys) so it stays correct if this surface ever gains more filter types.
  const hasAppliedFilters =
    countActiveFilters({
      valueFilters,
      rangeFilters,
      search: appliedSearch,
      zipCodeFilter,
    }) > 0

  // Hide First Bite search types when the active filter (e.g. grocery stores)
  // has no FB data. Callers may still pass explicit `options` to override.
  const searchTypeOptions = useMemo(
    () => options ?? getMobileSearchTypeOptions(categoriesData, state.filters),
    [options, categoriesData, state.filters]
  )

  const handleBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleFilterClick = useCallback(() => {
    // Reset pending edits to the currently-applied filters before opening,
    // matching the desktop FilterModal contract.
    syncPendingFilters()
    setIsSortOpen(false)
    setIsFilterOpen(true)
  }, [syncPendingFilters])

  const handleSortClick = useCallback(() => {
    setIsFilterOpen(false)
    setIsSortOpen(true)
  }, [])

  const handleSaveFilterClick = () => setIsSaveFilterOpen(true)
  const handleSaveFilterClose = () => setIsSaveFilterOpen(false)

  const handleFilterClose = useCallback(() => {
    setIsFilterOpen(false)
  }, [])

  const handleFilterSubmit = useCallback(() => {
    // applyFilters commits pending -> applied; the store subscription in
    // useMobileSearchFilterStore then feeds the new filters into the search.
    applyFilters()
    setIsFilterOpen(false)
  }, [applyFilters])

  const handleSortClose = useCallback(() => {
    setIsSortOpen(false)
  }, [])

  const visibleResults = resultFilter
    ? state.results.filter(resultFilter)
    : state.results

  if (isFilterOpen) {
    return (
      <MobileFiltersModal
        title="Search Filters"
        onClose={handleFilterClose}
        onSubmit={handleFilterSubmit}
      >
        <MobileCompanySearchFilterset
          filterIdentifier={SEARCH_FILTER_IDENTIFIER}
        />
      </MobileFiltersModal>
    )
  }

  return (
    <div className="flex flex-col h-full overflow-hidden bg-white">
      {/* Back Button */}
      {!hideBack && (
        <BackButton onClick={handleBack}>
          <ChevronLeft size={20} />
          <span>Back</span>
        </BackButton>
      )}

      {/* Search Controls */}
      <div className="px-2 pt-3 pb-1.5 bg-white">
        <MobileSearchDropdown
          value={state.searchType}
          label={state.searchTypeLabel}
          onChange={setSearchType}
          options={searchTypeOptions}
        />
        <MobileSearchInput
          value={state.query}
          onChange={setQuery}
          onClear={clearSearch}
          onFilterClick={handleFilterClick}
          onSortClick={handleSortClick}
          onSaveClick={hasAppliedFilters ? handleSaveFilterClick : undefined}
          currentSort={state.sort}
          isSortOpen={isSortOpen}
        />
        <FilterChips
          store={filterStore}
          identifier={SEARCH_FILTER_IDENTIFIER}
          showActive
          clearAllButton
        />
      </div>

      {/* Results */}
      <MobileSearchResults
        results={visibleResults}
        total={state.total}
        query={state.query}
        searchType={state.searchType}
        isLoading={state.isLoading}
        isLoadingMore={state.isLoadingMore}
        hasMore={state.hasMore}
        error={state.error}
        onLoadMore={loadMore}
        isDebouncing={state.isDebouncing}
        onClick={onClick}
        selectedItemId={selectedItemId}
      />

      {/* Sort Panel - covers entire area between headers and footers */}
      <MobileSearchSort
        isOpen={isSortOpen}
        currentSort={state.sort}
        onClose={handleSortClose}
        onSelect={setSort}
      />

      {/* Save-filter dialog (FIR-7789). Rendered in the results view (not the
          filter modal) so its centered Dialog stacks cleanly, and it snapshots
          the applied filter state the store already holds. */}
      <CreateSavedFilterModal
        show={isSaveFilterOpen}
        handleClose={handleSaveFilterClose}
        identifier={SEARCH_FILTER_IDENTIFIER}
        filterStore={filterStore}
      />
    </div>
  )
}
